// =====================================================================
// Mon compte
// =====================================================================
// Les deux droits que le RGPD rend obligatoires — récupérer ses données,
// effacer son compte — et la confirmation d'adresse.
//
// L'effacement mérite une explication franche à l'utilisateur, parce
// qu'il ne se comporte pas comme il s'y attend : son nœud fait partie de
// l'arbre des autres, et le supprimer couperait leur lignée. Il est donc
// anonymisé, pas détruit. Le lui cacher serait malhonnête.
// =====================================================================

import { useEffect, useState } from 'react';
import { api, ErreurApi, viderCachesPrives } from '../api';
import type { CommandeLivre, Contestation, Moi } from '../../partage/types';
import {
  Alerte,
  FormulaireIdentite,
  identiteValide,
  nomComplet,
} from '../composants/base';
import type { Identite } from '../../partage/types';
import { Avatar } from '../composants/Avatar';

export function MonCompte({
  moi,
  onDeconnecte,
  onRafraichir,
  onVoirTexte,
}: {
  moi: Moi;
  onDeconnecte: () => void;
  onRafraichir: () => void;
  onVoirTexte: (quoi: 'confidentialite' | 'conditions') => void;
}) {
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [etapeSuppression, setEtapeSuppression] = useState(0);
  const [saisie, setSaisie] = useState('');
  const [solde, setSolde] = useState<{ solde_centimes: number; devise: string } | null>(null);
  const [edition, setEdition] = useState(false);
  const [fiche, setFiche] = useState<Identite>({});
  const [livres, setLivres] = useState<CommandeLivre[] | null>(null);
  const [prixLivre, setPrixLivre] = useState(0);
  const [achat, setAchat] = useState(false);
  const [contestations, setContestations] = useState<Contestation[]>([]);
  const [codeDemande, setCodeDemande] = useState(false);
  const [code, setCode] = useState('');

  /** Les montants sont des entiers de centimes : on ne divise qu'ici, à
   *  l'affichage, et jamais dans un calcul. */
  const montant = (centimes: number) =>
    `${formater(centimes)} ${solde?.devise ?? 'USD'}`;

  async function chargerSolde() {
    try {
      const p = await api.portefeuille();
      setSolde({ solde_centimes: p.solde_centimes, devise: p.devise });
    } catch {
      setSolde(null);
    }
  }

  async function chargerLivres() {
    try {
      const r = await api.livres();
      setLivres(r.livres);
      setPrixLivre(r.prix_centimes);
    } catch {
      setLivres([]);
    }
  }

  async function chargerContestations() {
    try {
      const r = await api.filiations();
      setContestations(r.contestations);
    } catch {
      setContestations([]);
    }
  }

  useEffect(() => {
    void chargerSolde();
    void chargerLivres();
    void chargerContestations();
  }, []);

  async function retablir(id: string) {
    setErreur(null);
    setMessage(null);
    try {
      await api.annulerContestation(id);
      setMessage('Le lien est rétabli.');
      await chargerContestations();
      onRafraichir();
    } catch (e) {
      setErreur(
        e instanceof ErreurApi ? e.message : 'Rétablissement impossible.',
      );
    }
  }

  async function commander() {
    setErreur(null);
    setMessage(null);
    setAchat(true);
    try {
      const r = await api.acheterLivre();
      setMessage(
        `Votre livre est prêt : ${r.personnes} personne${
          r.personnes > 1 ? 's' : ''
        }. Vous pouvez le télécharger ci-dessous.`,
      );
      await Promise.all([chargerSolde(), chargerLivres()]);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Commande impossible.');
    } finally {
      setAchat(false);
    }
  }

  async function ouvrirLivre(id: string) {
    setErreur(null);
    try {
      const blob = await api.telechargerLivre(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'livre-de-famille.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErreur(
        e instanceof ErreurApi ? e.message : 'Téléchargement impossible.',
      );
    }
  }

  async function enregistrerFiche(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setOccupe(true);
    try {
      await api.modifierMaFiche(fiche);
      setMessage('Votre fiche est à jour.');
      setEdition(false);
      onRafraichir();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  async function demanderCode() {
    setErreur(null);
    setOccupe(true);
    try {
      await api.renvoyerVerification();
      setCodeDemande(true);
      setMessage('Un code vient de partir vers votre adresse.');
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Envoi impossible.');
    } finally {
      setOccupe(false);
    }
  }

  async function confirmerCode(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setOccupe(true);
    try {
      await api.verifierCode(moi.email, code);
      setMessage('Votre adresse est confirmée.');
      setCodeDemande(false);
      setCode('');
      onRafraichir();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Code invalide.');
    } finally {
      setOccupe(false);
    }
  }

  async function telecharger() {
    setErreur(null);
    setOccupe(true);
    try {
      const classeur = await api.mesDonnees();
      const url = URL.createObjectURL(classeur);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mes-donnees-yambi.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Export impossible.');
    } finally {
      setOccupe(false);
    }
  }

  async function supprimer() {
    setErreur(null);
    setOccupe(true);
    try {
      await api.supprimerMonCompte();
      // Le compte est effacé côté serveur ; on efface aussi ce qui reste
      // sur l'appareil, comme à une déconnexion.
      await viderCachesPrives();
      onDeconnecte();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Suppression impossible.');
      setOccupe(false);
    }
  }

  return (
    <>
      <h2 className="titre-page">Mon compte</h2>

      {erreur && <Alerte>{erreur}</Alerte>}
      {message && <Alerte type="succes">{message}</Alerte>}

      <div className="carte">
        <div className="masque-entete">
          <Avatar id={moi.personne_id} sexe={moi.sexe} taille={54} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{nomComplet(moi.nom, moi.prenoms)}</div>
            <div className="doux">{moi.email}</div>
          </div>
        </div>

        {/* Rien n'est proposé quand aucun code ne peut partir : un bouton
            qui ne mène nulle part est pire que pas de bouton. */}
        {!moi.email_verifie && moi.verification_disponible && (
          <>
            <p className="doux">
              Votre adresse n'est pas encore confirmée. Sans elle, vous ne
              pourrez pas récupérer votre compte si vous perdez votre mot de
              passe.
            </p>
            {!codeDemande ? (
              <button className="bouton mince" onClick={demanderCode} disabled={occupe}>
                M'envoyer un code
              </button>
            ) : (
              <form onSubmit={confirmerCode} style={{ marginTop: 12 }} noValidate>
                <div className="champ">
                  <label htmlFor="code-confirmation">
                    Le code à six chiffres reçu par courriel
                  </label>
                  <input
                    id="code-confirmation"
                    className="champ-code"
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <div className="rangee-boutons">
                  <button
                    type="button"
                    className="bouton secondaire"
                    onClick={demanderCode}
                    disabled={occupe}
                  >
                    Renvoyer
                  </button>
                  <button
                    className="bouton"
                    type="submit"
                    disabled={code.length !== 6 || occupe}
                  >
                    Confirmer
                  </button>
                </div>
              </form>
            )}
          </>
        )}
        {moi.email_verifie && <p className="doux">Adresse confirmée.</p>}

        {!edition ? (
          <button
            className="bouton secondaire mince"
            style={{ marginTop: 12 }}
            onClick={() => {
              setFiche({
                nom: moi.nom ?? '',
                prenoms: moi.prenoms ?? '',
                sexe: moi.sexe ?? undefined,
              });
              setEdition(true);
            }}
          >
            Corriger mes informations
          </button>
        ) : (
          <form onSubmit={enregistrerFiche} style={{ marginTop: 14 }} noValidate>
            <p className="doux" style={{ marginBottom: 13 }}>
              Ici vous pouvez tout corriger, y compris ce qui est déjà
              renseigné : c'est votre fiche.
            </p>
            <FormulaireIdentite valeur={fiche} onChange={setFiche} />
            <div className="rangee-boutons">
              <button
                type="button"
                className="bouton secondaire"
                onClick={() => setEdition(false)}
                disabled={occupe}
              >
                Annuler
              </button>
              <button
                className="bouton"
                type="submit"
                disabled={occupe || !identiteValide(fiche) || !fiche.nom?.trim()}
              >
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>

      <h3>Mon portefeuille</h3>
      <div className="carte">
        {solde === null ? (
          <p className="doux">Chargement…</p>
        ) : (
          <>
            <div className="solde">
              {formater(solde.solde_centimes)}{' '}
              <span className="solde-devise">{solde.devise}</span>
            </div>
            <p className="doux">
              Le rechargement par illicocash et mobile money arrive
              prochainement.
            </p>
          </>
        )}
      </div>

      {contestations.length > 0 && (
        <>
          <h3>Liens que j'ai retirés</h3>
          <div className="carte">
            <p className="doux" style={{ marginBottom: 13 }}>
              Se tromper en contestant ne doit pas être plus définitif que se
              tromper en déclarant. Chacun de ces liens peut revenir.
            </p>
            {contestations.map((c) => (
              <div key={c.id} className="ligne-livre">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 560 }}>
                    {nomComplet(c.autre_nom, c.autre_prenoms)}
                  </div>
                  <div className="doux">
                    {c.je_suis_enfant
                      ? `déclaré comme ${c.role_parent === 'pere' ? 'mon père' : 'ma mère'}`
                      : 'déclaré comme mon enfant'}
                    {c.statut === 'annulee'
                      ? ' · rétabli'
                      : c.retablissable
                        ? ''
                        : ' · un autre lien occupe la place'}
                  </div>
                </div>
                {c.statut === 'retiree' && c.retablissable && (
                  <button
                    className="bouton secondaire mince"
                    onClick={() => retablir(c.id)}
                  >
                    Rétablir
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <h3>Mon livre de famille</h3>
      <div className="carte">
        <p className="doux" style={{ marginBottom: 13 }}>
          Un document à imprimer ou à offrir : l'arbre dessiné, puis une fiche
          par proche — grands-parents, oncles, cousins, neveux. Il est{' '}
          <strong>arrêté au jour de la commande</strong> : votre famille
          continuera de s'agrandir, ce livre-là restera celui d'aujourd'hui.
        </p>

        <button
          className="bouton"
          onClick={commander}
          disabled={
            achat ||
            livres === null ||
            (solde !== null && solde.solde_centimes < prixLivre)
          }
        >
          {achat
            ? 'Préparation…'
            : `Commander mon livre — ${montant(prixLivre)}`}
        </button>

        {solde !== null && solde.solde_centimes < prixLivre && (
          <p className="doux" style={{ marginTop: 10 }}>
            Votre solde ne suffit pas. Rechargez votre portefeuille.
          </p>
        )}

        {livres !== null && livres.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <p className="doux" style={{ marginBottom: 10 }}>
              Vos livres
            </p>
            {livres.map((l) => (
              <div key={l.id} className="ligne-livre">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 560 }}>
                    {new Date(l.cree_le).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                  <div className="doux">
                    {l.statut === 'produite'
                      ? `${l.personnes} personne${(l.personnes ?? 0) > 1 ? 's' : ''} · ${Math.round(l.taille / 1024)} Ko`
                      : l.statut === 'echouee'
                        ? 'Non produit · remboursé'
                        : 'En préparation'}
                  </div>
                </div>
                {l.statut === 'produite' && (
                  <button
                    className="bouton secondaire mince"
                    onClick={() => ouvrirLivre(l.id)}
                  >
                    Télécharger
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <h3>Mes données</h3>
      <div className="carte">
        <p className="doux" style={{ marginBottom: 13 }}>
          Récupérez tout ce que Yambi détient sur vous, dans un classeur à
          ouvrir dans Excel : votre famille, votre compte, les personnes et
          les liens que vous avez déclarés, vos rapprochements, vos messages
          et votre portefeuille.
        </p>
        <button className="bouton secondaire" onClick={telecharger} disabled={occupe}>
          Télécharger mes données (Excel)
        </button>
      </div>

      <h3>Textes</h3>
      <div className="carte" style={{ display: 'grid', gap: 8 }}>
        <button className="bouton secondaire" onClick={() => onVoirTexte('confidentialite')}>
          Politique de confidentialité
        </button>
        <button className="bouton secondaire" onClick={() => onVoirTexte('conditions')}>
          Conditions d'utilisation
        </button>
      </div>

      <h3>Supprimer mon compte</h3>
      <div className="carte">
        {etapeSuppression === 0 && (
          <>
            <p className="doux" style={{ marginBottom: 13 }}>
              Votre compte, votre adresse et vos messages seront effacés
              définitivement.
            </p>
            <button
              className="bouton danger"
              onClick={() => setEtapeSuppression(1)}
            >
              Supprimer mon compte
            </button>
          </>
        )}

        {etapeSuppression === 1 && (
          <>
            <p>
              <strong>Une chose à savoir avant de continuer.</strong>
            </p>
            <p className="doux">
              Votre fiche fait partie de l'arbre de vos proches. La supprimer
              couperait leur lignée — leurs données à eux. Elle sera donc{' '}
              <strong>rendue anonyme</strong> : votre nom disparaît, la place
              que vous occupez dans la famille reste, sans vous nommer.
            </p>
            <p className="doux" style={{ marginBottom: 14 }}>
              Si personne n'est rattaché à vous, votre fiche est entièrement
              supprimée. Cette opération est irréversible.
            </p>
            <div className="rangee-boutons">
              <button
                className="bouton secondaire"
                onClick={() => setEtapeSuppression(0)}
              >
                Annuler
              </button>
              <button className="bouton danger" onClick={() => setEtapeSuppression(2)}>
                J'ai compris
              </button>
            </div>
          </>
        )}

        {etapeSuppression === 2 && (
          <>
            <div className="champ">
              <label htmlFor="confirmer">
                Écrivez SUPPRIMER pour confirmer
              </label>
              <input
                id="confirmer"
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
                autoComplete="off"
                autoCapitalize="characters"
              />
            </div>
            <div className="rangee-boutons">
              <button
                className="bouton secondaire"
                onClick={() => {
                  setEtapeSuppression(0);
                  setSaisie('');
                }}
                disabled={occupe}
              >
                Annuler
              </button>
              <button
                className="bouton danger"
                onClick={supprimer}
                disabled={saisie.trim().toUpperCase() !== 'SUPPRIMER' || occupe}
              >
                {occupe ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        <button
          className="bouton secondaire"
          onClick={() => {
            void api.deconnexion().finally(onDeconnecte);
            onRafraichir();
          }}
        >
          Me déconnecter
        </button>
      </div>
    </>
  );
}

function formater(centimes: number): string {
  return (centimes / 100).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
